import React from 'react';
import ReactDOM from 'react-dom';
import {createMemoryHistory} from 'history';
import {useRouterHistory} from 'react-router';
import ReduxProvider from './components/ReduxProvider';
import Redispatcher from './services/redispatcher';
import JsonAPI from './services/jsonapi';
import IssuesV2Api from './services/issues-v2-api';
import docsSDKWrapper from './services/docsSDKWrapper';
import {getCurrentToken} from './services/getCurrentToken';
import createStore from './app/store';
import {parseQueryString, stringifyQuery} from './utils/query-helpers';
import featureFlags, {initialize as initializeFF, FEATURE_FLAGS} from './utils/featureFlags';
import {initEnvConfig, ENV_CONFIGS, ISSUES_ENV_TYPES} from './constants/envs';
import {TYPE_ISSUES, TYPE_QUALITY_ISSUES} from './constants/issue-types';
import {viewTypes} from './constants/view-types';
import {
  configureJsonApiAction,
  configureOxygenAction,
  configureDocumentAction,
  configureSheetsAction,
  setTargetUrnAction,
  changeLocationAction,
  setDocumentThumbnailUrlPromiseAction,
  setFeatureFlagAction,
  setCurrentProject,
  configureAnalyticsAction,
  setCurrentViewAction,
  setIssueIdsFilterAction,
  setFilterCallbackAction,
  selectPushpinAction
} from './app/actions';
import {getCurrentPreviewIssueAttributes} from './app/selectors';
import {setLanguage, setLocale} from './utils/i18n';
import {version} from '../package.json';

import {createAnalyticsManager} from './utils/analytics-helpers';
import {issueLink} from './utils/issues-helpers';
import lodashGet from 'lodash/get';
import AdminApi from './services/admin-api';

export default class {
  static get events() {
    return Redispatcher.events;
  }

  constructor() {
    // This is the only thing that needs to be defined, here, b/c
    // it enables events to be immediately registered, regardless of mount pending status
    this.redispatcher = new Redispatcher();
    this.getState = this.getState.bind(this);
    this.dispatch = this.dispatch.bind(this);
  }

  /*
   *
   * External API for the containing app to interact with issues-ui
   *
   */

  on(eventName, callback) {
    return this.redispatcher.on(eventName, callback);
  }

  setPath(path) {
    // If unified issues flag is on, we should force use of "quality-issues" (phase 1 unified issues API) if "issues" is requested
    let manipulatedPath = path;
    if (this.viewType === viewTypes.DOCUMENT || this.viewType === viewTypes.PROJECT) {
      manipulatedPath = path.replace(`/${TYPE_ISSUES}`, `/${TYPE_QUALITY_ISSUES}`);
    }
    return Promise.resolve(this.store.dispatch(changeLocationAction(manipulatedPath)));
  }

  isInEditOrNewProcess() {
    return this.store.getState().isCreatingIssue;
  }

  dispatch(action) {
    return this.store.dispatch(action);
  }

  getState() {
    return this.store.getState();
  }

  setTargetUrn(targetUrn) {
    this.store.dispatch(setTargetUrnAction(targetUrn));
  }

  setSheetsConfig(sheetsConfig = {}) {
    const sheetsConfigObj = {
      sheetList: [],
      currentSheets: Array.isArray(sheetsConfig.currentSheet)
        ? sheetsConfig.currentSheet
        : [].concat(sheetsConfig.currentSheet)
    };
    sheetsConfigObj.currentSheets = sheetsConfigObj.currentSheets.map(currSheet => {
      const matchedSheet = sheetsConfig.sheetList?.find(sheet => sheet.guid === lodashGet(currSheet, 'sheetGuid'));
      return {...currSheet, viewableId: currSheet.viewableID || matchedSheet?.viewableID};
    });
    // Simplify sheets objects in sheetList
    if (sheetsConfig && sheetsConfig.sheetList) {
      sheetsConfig.sheetList.forEach(sheet => {
        sheetsConfigObj.sheetList.push({
          sheetGuid: sheet.guid,
          sheetName: sheet.name,
          viewableId: sheet.viewableID
        });
      });
    }

    this.store.dispatch(configureSheetsAction(sheetsConfigObj));
    const issue = getCurrentPreviewIssueAttributes(this.getState());
    Object.keys(issue).length && this.store.dispatch(selectPushpinAction(issue));
  }

  setDocumentThumbnailUrlPromise(thumbnailPromise) {
    this.store.dispatch(setDocumentThumbnailUrlPromiseAction(thumbnailPromise));
  }

  setIssueIdsFilter(issueIdsFilter) {
    if (featureFlags.get(FEATURE_FLAGS.FILTER_ISSUES_BY_ID)) {
      this.store.dispatch(setIssueIdsFilterAction(issueIdsFilter));
    }
  }

  // The hosting app can register an optional filter callback. For example, the client
  // could then further filter the issues by objectId.
  setShouldIncludeIssueFn(filterCallback) {
    if (typeof filterCallback === 'function') {
      this.store.dispatch(setFilterCallbackAction(filterCallback));
    }
  }

  unmount(domElement) {
    // boolean unmountComponentAtNode(DOMElement container)
    //   Remove a mounted React component from the DOM and clean up its event handlers
    //   and state. If no component was mounted in the container, calling this function
    //   does nothing. Returns true if a component was unmounted and false if there was
    //   no component to unmount.
    return ReactDOM.unmountComponentAtNode(domElement);
  }

  configureExternalControls() {
    this.pushpinsControl = null;
  }

  _initializeApp(
    targetDomElement,
    token,
    {
      apiConfig,
      oxygenMappings,
      documentConfig,
      isIssuesEnabled,
      isQualityIssuesEnabled,
      isTemplatesViewEnabled = false,
      isMinimalTableColumns = false,
      showProjectViewCreateButton = true,
      enablePushpinSnapshots = true,
      getIssueLink,
      analyticsConfig = {},
      currentProject = {},
      onError,
      issueIdsFilter
    } = {}
  ) {
    const configCopy = Object.assign({}, apiConfig);
    const env = configCopy.env;
    const region = configCopy.region;
    // this used to change the app language for testing purposes.
    //   for example type in your console console `localStorage.setItem("issuesLocale", "de"), location.reload()` for german or "es" for spanish.
    //   to remove type `localStorage.removeItem("issuesLocale"), location.reload()`
    if (env !== ISSUES_ENV_TYPES.PROD && localStorage && localStorage.issuesLocale) {
      configCopy.language = localStorage.issuesLocale;
    }

    setLanguage(configCopy.language);
    setLocale(configCopy.language);
    const appHistory = useRouterHistory(createMemoryHistory)({
      queryKey: false,
      parseQueryString,
      stringifyQuery
    });
    this.configureExternalControls();
    // eslint-disable-next-line new-cap
    const docsSDK = new docsSDKWrapper(ENV_CONFIGS.DOCS_SDK_CONFIG, token);

    const analyticsManager = createAnalyticsManager(region, configCopy.language);

    const shouldUseIssuesCOSV3 = featureFlags.get(FEATURE_FLAGS.USE_ISSUES_CLOUDOS_V3_BACKEND);
    const adminApi = new AdminApi(this.dispatch, this.getState);
    adminApi.setConfig(apiConfig);

    const store = createStore({
      redispatcher: this.redispatcher,
      jsonApi: new JsonAPI(this.dispatch, this.getState),
      issuesV2Api: new IssuesV2Api(this.dispatch, this.getState, shouldUseIssuesCOSV3),
      appHistory,
      pushpinsControl: this.pushpinsControl,
      docsSDK,
      analyticsManager,
      adminApi
    });
    this.store = store;

    if (this.pushpinsControl) {
      this.pushpinsControl.setDispatch(store.dispatch);
    }

    store.dispatch(setCurrentViewAction(this.viewType));
    store.dispatch(configureJsonApiAction(configCopy));
    // this can be here, as long as configureJsonApiAction is synchronous:
    if (!oxygenMappings) {
      const usersPromise = adminApi.getPaginatedUsersInProject();
      const rolesPromise = docsSDK.getRolesInProject(apiConfig.projectId);
      const companiesPromise = docsSDK.getCompaniesInProject(apiConfig.projectId);

      oxygenMappings = {
        usersPromise,
        rolesPromise,
        companiesPromise
      };
    }
    store.dispatch(configureOxygenAction(oxygenMappings));
    store.dispatch(setCurrentProject(currentProject));
    store.dispatch(configureAnalyticsAction(analyticsConfig));
    documentConfig && store.dispatch(configureDocumentAction(documentConfig));
    if (featureFlags.get(FEATURE_FLAGS.FILTER_ISSUES_BY_ID)) {
      issueIdsFilter && store.dispatch(setIssueIdsFilterAction(issueIdsFilter));
    }

    store.dispatch(
      setFeatureFlagAction({
        isIssuesEnabled,
        isQualityIssuesEnabled,
        isMinimalTableColumns,
        showProjectViewCreateButton,
        isTemplatesViewEnabled,
        enablePushpinSnapshots
      })
    );

    issueLink.func = getIssueLink;
    // Set a default for the current view type
    this.setPath(this.defaultRoute);

    return new Promise(resolve => {
      const props = {
        key: `${configCopy.containerId}_${new Date().getTime()}`,
        store,
        history: appHistory,
        viewType: this.viewType,
        locale: configCopy.language,
        env,
        region,
        currentProject,
        onError
      };
      ReactDOM.render(React.createElement(ReduxProvider, props), targetDomElement, () => {
        resolve();
      });
    });
  }

  mount(targetDomElement, options) {
    const {apiConfig, analyticsConfig = {}, currentProject = {}} = options;
    const apiConfigCopy = Object.assign({}, apiConfig);
    // Initializing ENV_CONFIG
    initEnvConfig(apiConfigCopy);

    const env = apiConfigCopy.env;
    const region = apiConfigCopy.region;
    const projectId = apiConfigCopy.projectId;
    // Support for both Docs and HQ project object
    const accountId = lodashGet(currentProject, 'account.id') || lodashGet(currentProject, 'accountId');
    const hostingAppName = lodashGet(analyticsConfig, 'hostingAppName');
    const [major, minor, patch] = version
      .match(/^(\d+)\.(\d+)\.(\d+)[^\d]?.*$/)
      .slice(1)
      .map(x => Number(x));

    const launchDarklyUserObject = {custom: {region, accountId, projectId, version, major, minor, patch}};
    const analyticsId = lodashGet(analyticsConfig, 'analyticsId');
    if (analyticsId) {
      launchDarklyUserObject.key = analyticsId;
    }
    if (hostingAppName) {
      launchDarklyUserObject.custom.hostingAppName = hostingAppName;
    }

    return new Promise(resolve => {
      // Init remote FFs
      Promise.all([initializeFF(env, region, launchDarklyUserObject), getCurrentToken(ENV_CONFIGS.DOCS)]).then(
        ([, token]) => {
          this._initializeApp(targetDomElement, token, options).then(() => {
            resolve();
          });
        }
      );
    });
  }
}
